import React from 'react';
import { ShoppingCart, Trash2, Plus, Minus, X, MapPin, Clock, Truck, Store } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCart } from '@/context/CartContext';
import { formatPrice, formatPricePerKg } from '@/lib/formatPrice';

export const CartSheet = () => {
  const {
    items,
    removeItem,
    updateQuantity,
    clearCart,
    subtotal,
    total,
    itemCount,
    isEmpty,
    fulfillmentType,
    setFulfillmentType,
    deliveryFee,
    isCartOpen,
    closeCart
  } = useCart();

  return (
    <Sheet open={isCartOpen} onOpenChange={closeCart}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="p-4 pb-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-instagram-purple" />
              Il tuo Carrello
              {itemCount > 0 && (
                <Badge className="gradient-instagram text-primary-foreground">
                  {itemCount}
                </Badge>
              )}
            </SheetTitle>
          </div>
        </SheetHeader>

        {isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <ShoppingCart className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">Carrello vuoto</h3>
            <p className="text-sm text-muted-foreground">
              Aggiungi piatti dal menu per iniziare il tuo ordine
            </p>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-card border border-border rounded-lg p-3"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0 pr-2">
                        <h4 className="font-medium text-foreground text-sm leading-tight">
                          {item.name}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.isFixedPrice ? 'Prezzo fisso' : `${item.grams}g • ${formatPricePerKg(item.pricePerKg)}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center font-medium text-foreground">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <span className="font-bold gradient-instagram-text">
                        {formatPrice(item.lineTotal)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t border-border p-4 space-y-3 bg-muted/30">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Modalita ordine
                </p>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFulfillmentType('PICKUP')}
                    className={`h-11 rounded-2xl justify-center gap-2 ${
                      fulfillmentType === 'PICKUP'
                        ? 'border-instagram-purple bg-instagram-purple/10 text-instagram-purple hover:bg-instagram-purple/15'
                        : 'border-border bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    <Store className="w-4 h-4" />
                    Ritiro in sede
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFulfillmentType('DELIVERY')}
                    className={`h-11 rounded-2xl justify-center gap-2 ${
                      fulfillmentType === 'DELIVERY'
                        ? 'border-success bg-success/10 text-success hover:bg-success/15'
                        : 'border-border bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    <Truck className="w-4 h-4" />
                    Consegna
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  {fulfillmentType === 'PICKUP' ? (
                    <Badge variant="outline" className="flex items-center gap-1.5 py-1 px-3 border-instagram-purple text-instagram-purple">
                      <Store className="w-3.5 h-3.5" />
                      Ritiro in sede
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="flex items-center gap-1.5 py-1 px-3 border-success text-success">
                      <Truck className="w-3.5 h-3.5" />
                      Consegna a domicilio
                    </Badge>
                  )}
                </div>
              </div>

              <Separator />

              {/* Subtotal */}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotale</span>
                <span className="font-medium text-foreground">{formatPrice(subtotal)}</span>
              </div>

              {/* Delivery fee if applicable */}
              {fulfillmentType === 'DELIVERY' && deliveryFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Consegna</span>
                  <span className="font-medium text-foreground">{formatPrice(deliveryFee)}</span>
                </div>
              )}

              {fulfillmentType === 'PICKUP' && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Ritiro</span>
                  <span className="font-medium text-success">Gratuito</span>
                </div>
              )}

              <Separator />

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Totale</span>
                <span className="text-2xl font-bold gradient-instagram-text">
                  {formatPrice(total)}
                </span>
              </div>

              {/* Clear cart button */}
              <Button
                variant="outline"
                className="w-full text-destructive hover:bg-destructive/10"
                onClick={clearCart}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Svuota carrello
              </Button>
            </div>
          </>
        )}

        <SheetFooter className="p-4 pt-0">
          {!isEmpty && (
            <Button
              className="w-full gradient-instagram text-primary-foreground hover:opacity-90 shadow-instagram"
              size="lg"
              onClick={() => {
                closeCart();
                // Navigate to checkout
                window.location.href = '#checkout';
              }}
            >
              Procedi all'ordine
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
